/**
 * Boot-only redirect from the raw Firebase Hosting hostnames to the custom
 * domain. Production only.
 *
 * This module is the first import in main.tsx and has no imports of its own,
 * so it evaluates before App.tsx, firebase.ts, Sentry, analytics or anything
 * else. ES imports are hoisted: code written inline at the top of main.tsx
 * would run after every imported module had already evaluated, which is why
 * this lives in its own file.
 *
 * It runs exactly once, at page load. There are no listeners and no timers, so
 * nothing here can fire mid-session. At load nothing is in memory yet, so a
 * coach's in-editor work cannot be lost by it; editor drafts live in Firestore.
 *
 * Only the two production default hostnames are matched, exactly. Staging,
 * preview channels and localhost are never touched. Paths under /__/ are the
 * Firebase Auth handler and its helpers, served by Hosting itself, and are
 * left alone.
 *
 * Referral credit: the passive referral (services/referrals.ts) is a
 * localStorage entry, which does not cross origins. It is carried in a query
 * parameter and written back on landing with the same value, so its storedAt
 * and therefore its remaining TTL are unchanged. The parameter is then removed
 * from the address bar.
 */

const OLD_HOSTS = ['double-bagel-9f766.web.app', 'double-bagel-9f766.firebaseapp.com'];
const NEW_HOST = 'app.doublebagel.net';
const REFERRAL_KEY = 'db_referral';   // same key as services/referrals.ts
const REFERRAL_PARAM = 'db_referral';

(function bootRedirect() {
  const { hostname, pathname, search, hash } = window.location;

  // Landing side: restore a carried referral, then clean the address bar.
  if (hostname === NEW_HOST) {
    const params = new URLSearchParams(search);
    const carried = params.get(REFERRAL_PARAM);
    if (carried === null) return;
    try {
      const parsed: unknown = JSON.parse(carried);
      if (parsed && typeof parsed === 'object' && typeof (parsed as { storedAt?: unknown }).storedAt === 'string') {
        localStorage.setItem(REFERRAL_KEY, carried);
      }
    } catch {
      // Malformed or storage unavailable: nothing to restore.
    }
    params.delete(REFERRAL_PARAM);
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', pathname + (rest ? `?${rest}` : '') + hash);
    return;
  }

  // Redirect side.
  if (!OLD_HOSTS.includes(hostname)) return;
  if (pathname.startsWith('/__/')) return;

  // Append rather than rebuild the query string, so existing parameters keep
  // their exact encoding.
  let query = search;
  try {
    const raw = localStorage.getItem(REFERRAL_KEY);
    if (raw) query += `${search ? '&' : '?'}${REFERRAL_PARAM}=${encodeURIComponent(raw)}`;
  } catch {
    // localStorage unavailable: redirect without it.
  }

  window.location.replace(`https://${NEW_HOST}${pathname}${query}${hash}`);
})();
